<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useI18n } from 'vue-i18n';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import {
  askBrowserNotificationPermission,
  showTestNotification,
} from 'dashboard/helper/browserNotifications';
import { requestPushPermissions } from 'dashboard/helper/pushHelper';
import NotificationsAPI from 'dashboard/api/notifications';
import AgentAPI from 'dashboard/api/agents';

const { t } = useI18n();
const store = useStore();
const { accountId } = useAccount();

const isSaving = ref(false);
const isTestingNotifications = ref(false);

const currentAccount = computed(
  () => store.getters['accounts/getAccount'](accountId.value) || {}
);

const accountSettings = computed(() => currentAccount.value.settings || {});

const assignmentEmailEnabled = ref(true);
const zohoTourEnabled = ref(false);
const browserNotificationsEnabled = ref(false);
const browserNotificationsRoles = ref([]);
const templatesSectionEnabled = ref(false);
const templatesSectionRoles = ref([]);
const gestionPlusEnabled = ref(false);
const gestionPlusRoles = ref([]);
const markAsReadToggleEnabled = ref(false);
const markAsReadToggleRoles = ref([]);
const crmNotesSyncEnabled = ref(false);
const crmNotesSyncRoles = ref([]);
const aiBackfillEnabled = ref(false);
const aiBackfillRoles = ref([]);
const autoTranslationEnabled = ref(false);
const autoTranslationRoles = ref([]);
const autoTranslationTestConversationId = ref('');

// Categorías de plantillas de WhatsApp (Twilio) visibles para los agentes.
// Se guarda como array en settings.template_visible_categories; sin
// configurar (null) el backend muestra todas.
const TEMPLATE_CATEGORIES = ['utility', 'marketing', 'authentication'];
const templateVisibleCategories = ref([...TEMPLATE_CATEGORIES]);
const templateCategoryOptions = computed(() => [
  {
    id: 'utility',
    title: t('FEATURES_SETTINGS.TEMPLATE_CATEGORIES.UTILITY.TITLE'),
    description: t('FEATURES_SETTINGS.TEMPLATE_CATEGORIES.UTILITY.DESCRIPTION'),
  },
  {
    id: 'marketing',
    title: t('FEATURES_SETTINGS.TEMPLATE_CATEGORIES.MARKETING.TITLE'),
    description: t(
      'FEATURES_SETTINGS.TEMPLATE_CATEGORIES.MARKETING.DESCRIPTION'
    ),
  },
  {
    id: 'authentication',
    title: t('FEATURES_SETTINGS.TEMPLATE_CATEGORIES.AUTHENTICATION.TITLE'),
    description: t(
      'FEATURES_SETTINGS.TEMPLATE_CATEGORIES.AUTHENTICATION.DESCRIPTION'
    ),
  },
]);
const readTemplateVisibleCategories = s =>
  Array.isArray(s.template_visible_categories)
    ? s.template_visible_categories
    : [...TEMPLATE_CATEGORIES];
const isTemplateCategoryVisible = category =>
  templateVisibleCategories.value.includes(category);

const customRoles = computed(() => store.getters['customRole/getCustomRoles']);

// Standard roles + custom roles available as options
const roleOptions = computed(() => {
  const standard = [
    {
      id: 'agent',
      name: t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.STANDARD_ROLES.AGENT'),
    },
    {
      id: 'administrator',
      name: t(
        'FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.STANDARD_ROLES.ADMINISTRATOR'
      ),
    },
  ];
  const custom = customRoles.value.map(r => ({
    id: String(r.id),
    name: r.name,
  }));
  return [...standard, ...custom];
});

const toggleRole = id => {
  const idx = browserNotificationsRoles.value.indexOf(id);
  if (idx === -1) browserNotificationsRoles.value.push(id);
  else browserNotificationsRoles.value.splice(idx, 1);
};

const toggleTemplatesRole = id => {
  const idx = templatesSectionRoles.value.indexOf(id);
  if (idx === -1) templatesSectionRoles.value.push(id);
  else templatesSectionRoles.value.splice(idx, 1);
};

const toggleGestionPlusRole = id => {
  const idx = gestionPlusRoles.value.indexOf(id);
  if (idx === -1) gestionPlusRoles.value.push(id);
  else gestionPlusRoles.value.splice(idx, 1);
};

const toggleMarkAsReadRole = id => {
  const idx = markAsReadToggleRoles.value.indexOf(id);
  if (idx === -1) markAsReadToggleRoles.value.push(id);
  else markAsReadToggleRoles.value.splice(idx, 1);
};

const toggleCrmNotesSyncRole = id => {
  const idx = crmNotesSyncRoles.value.indexOf(id);
  if (idx === -1) crmNotesSyncRoles.value.push(id);
  else crmNotesSyncRoles.value.splice(idx, 1);
};

const toggleAiBackfillRole = id => {
  const idx = aiBackfillRoles.value.indexOf(id);
  if (idx === -1) aiBackfillRoles.value.push(id);
  else aiBackfillRoles.value.splice(idx, 1);
};

const toggleAutoTranslationRole = id => {
  const idx = autoTranslationRoles.value.indexOf(id);
  if (idx === -1) autoTranslationRoles.value.push(id);
  else autoTranslationRoles.value.splice(idx, 1);
};

onMounted(async () => {
  const s = accountSettings.value;
  // eslint-disable-next-line no-console
  console.log(
    '[Features] accountSettings on mount:',
    JSON.parse(JSON.stringify(s))
  );

  assignmentEmailEnabled.value =
    s.assignment_email_enabled == null
      ? true
      : s.assignment_email_enabled !== false &&
        s.assignment_email_enabled !== 'false';
  zohoTourEnabled.value =
    s.zoho_tour_enabled === true || s.zoho_tour_enabled === 'true';

  browserNotificationsEnabled.value =
    s.browser_notifications_enabled === true ||
    s.browser_notifications_enabled === 'true';
  browserNotificationsRoles.value = Array.isArray(s.browser_notifications_roles)
    ? s.browser_notifications_roles
    : [];

  templatesSectionEnabled.value =
    s.templates_section_enabled === true ||
    s.templates_section_enabled === 'true';
  templatesSectionRoles.value = Array.isArray(s.templates_section_roles)
    ? s.templates_section_roles
    : [];

  gestionPlusEnabled.value =
    s.gestion_plus_enabled === true || s.gestion_plus_enabled === 'true';
  gestionPlusRoles.value = Array.isArray(s.gestion_plus_roles)
    ? s.gestion_plus_roles
    : [];

  markAsReadToggleEnabled.value =
    s.mark_as_read_toggle_enabled === true ||
    s.mark_as_read_toggle_enabled === 'true';
  markAsReadToggleRoles.value = Array.isArray(s.mark_as_read_toggle_roles)
    ? s.mark_as_read_toggle_roles
    : [];

  crmNotesSyncEnabled.value =
    s.crm_notes_sync_enabled === true || s.crm_notes_sync_enabled === 'true';
  crmNotesSyncRoles.value = Array.isArray(s.crm_notes_sync_roles)
    ? s.crm_notes_sync_roles
    : [];

  aiBackfillEnabled.value =
    s.ai_backfill_enabled === true || s.ai_backfill_enabled === 'true';
  aiBackfillRoles.value = Array.isArray(s.ai_backfill_roles)
    ? s.ai_backfill_roles
    : [];

  autoTranslationEnabled.value =
    s.auto_translation_enabled === true ||
    s.auto_translation_enabled === 'true';
  autoTranslationRoles.value = Array.isArray(s.auto_translation_roles)
    ? s.auto_translation_roles
    : [];
  autoTranslationTestConversationId.value =
    s.auto_translation_test_conversation_id == null
      ? ''
      : String(s.auto_translation_test_conversation_id);
  templateVisibleCategories.value = readTemplateVisibleCategories(s);

  // eslint-disable-next-line no-console
  console.log(
    '[Features] browserNotificationsRoles loaded:',
    browserNotificationsRoles.value
  );
  // eslint-disable-next-line no-console
  console.log(
    '[Features] templatesSectionRoles loaded:',
    templatesSectionRoles.value
  );
  // eslint-disable-next-line no-console
  console.log('[Features] gestionPlusRoles loaded:', gestionPlusRoles.value);

  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('customRole/getCustomRole'),
  ]);
});

const saveToggle = async (key, value) => {
  isSaving.value = true;
  try {
    await store.dispatch('accounts/update', { [key]: value });
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
  } catch {
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const toggleTemplateCategory = async category => {
  const next = isTemplateCategoryVisible(category)
    ? templateVisibleCategories.value.filter(c => c !== category)
    : [...templateVisibleCategories.value, category];
  templateVisibleCategories.value = next;
  await saveToggle('template_visible_categories', next);
};

watch(browserNotificationsEnabled, val => {
  if (val) {
    askBrowserNotificationPermission();
    // Also request the Web Push subscription, so admins get notified even
    // when the tab is not focused. Idempotent: if already subscribed, no-op.
    requestPushPermissions({ onSuccess: () => {} });
  }
});

// Re-sync local state when the store is updated after saving
watch(accountSettings, s => {
  // eslint-disable-next-line no-console
  console.log(
    '[Features] accountSettings changed in store:',
    JSON.parse(JSON.stringify(s))
  );
  browserNotificationsEnabled.value =
    s.browser_notifications_enabled === true ||
    s.browser_notifications_enabled === 'true';
  browserNotificationsRoles.value = Array.isArray(s.browser_notifications_roles)
    ? s.browser_notifications_roles
    : [];
  templatesSectionEnabled.value =
    s.templates_section_enabled === true ||
    s.templates_section_enabled === 'true';
  templatesSectionRoles.value = Array.isArray(s.templates_section_roles)
    ? s.templates_section_roles
    : [];
  gestionPlusEnabled.value =
    s.gestion_plus_enabled === true || s.gestion_plus_enabled === 'true';
  gestionPlusRoles.value = Array.isArray(s.gestion_plus_roles)
    ? s.gestion_plus_roles
    : [];
  markAsReadToggleEnabled.value =
    s.mark_as_read_toggle_enabled === true ||
    s.mark_as_read_toggle_enabled === 'true';
  markAsReadToggleRoles.value = Array.isArray(s.mark_as_read_toggle_roles)
    ? s.mark_as_read_toggle_roles
    : [];
  crmNotesSyncEnabled.value =
    s.crm_notes_sync_enabled === true || s.crm_notes_sync_enabled === 'true';
  crmNotesSyncRoles.value = Array.isArray(s.crm_notes_sync_roles)
    ? s.crm_notes_sync_roles
    : [];

  aiBackfillEnabled.value =
    s.ai_backfill_enabled === true || s.ai_backfill_enabled === 'true';
  aiBackfillRoles.value = Array.isArray(s.ai_backfill_roles)
    ? s.ai_backfill_roles
    : [];
  templateVisibleCategories.value = readTemplateVisibleCategories(s);
  // eslint-disable-next-line no-console
  console.log(
    '[Features] browserNotificationsRoles after sync:',
    browserNotificationsRoles.value
  );
});

const saveGestionPlus = async () => {
  isSaving.value = true;
  const payload = {
    gestion_plus_enabled: gestionPlusEnabled.value,
    gestion_plus_roles: gestionPlusRoles.value,
  };
  try {
    await store.dispatch('accounts/update', payload);
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] saveGestionPlus error:', e);
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveMarkAsReadToggle = async () => {
  isSaving.value = true;
  const payload = {
    mark_as_read_toggle_enabled: markAsReadToggleEnabled.value,
    mark_as_read_toggle_roles: markAsReadToggleRoles.value,
  };
  // eslint-disable-next-line no-console
  console.log(
    '[Features] saveMarkAsReadToggle payload:',
    JSON.parse(JSON.stringify(payload))
  );
  try {
    await store.dispatch('accounts/update', payload);
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] saveMarkAsReadToggle error:', e);
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveCrmNotesSync = async () => {
  isSaving.value = true;
  const payload = {
    crm_notes_sync_enabled: crmNotesSyncEnabled.value,
    crm_notes_sync_roles: crmNotesSyncRoles.value,
  };
  // eslint-disable-next-line no-console
  console.log(
    '[Features] saveCrmNotesSync payload:',
    JSON.parse(JSON.stringify(payload))
  );
  try {
    await store.dispatch('accounts/update', payload);
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] saveCrmNotesSync error:', e);
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveAiBackfill = async () => {
  isSaving.value = true;
  const payload = {
    ai_backfill_enabled: aiBackfillEnabled.value,
    ai_backfill_roles: aiBackfillRoles.value,
  };
  // eslint-disable-next-line no-console
  console.log(
    '[Features] saveAiBackfill payload:',
    JSON.parse(JSON.stringify(payload))
  );
  try {
    await store.dispatch('accounts/update', payload);
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] saveAiBackfill error:', e);
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveAutoTranslation = async () => {
  isSaving.value = true;
  const payload = {
    auto_translation_enabled: autoTranslationEnabled.value,
    auto_translation_roles: autoTranslationRoles.value,
    auto_translation_test_conversation_id:
      autoTranslationTestConversationId.value.trim(),
  };
  // eslint-disable-next-line no-console
  console.log(
    '[Features] saveAutoTranslation payload:',
    JSON.parse(JSON.stringify(payload))
  );
  try {
    await store.dispatch('accounts/update', payload);
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] saveAutoTranslation error:', e);
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveTemplatesSection = async () => {
  isSaving.value = true;
  const payload = {
    templates_section_enabled: templatesSectionEnabled.value,
    templates_section_roles: templatesSectionRoles.value,
  };
  // eslint-disable-next-line no-console
  console.log(
    '[Features] saveTemplatesSection payload:',
    JSON.parse(JSON.stringify(payload))
  );
  try {
    const result = await store.dispatch('accounts/update', payload);
    // eslint-disable-next-line no-console
    console.log('[Features] saveTemplatesSection result:', result);
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] saveTemplatesSection error:', e);
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const saveBrowserNotifications = async () => {
  isSaving.value = true;
  const payload = {
    browser_notifications_enabled: browserNotificationsEnabled.value,
    browser_notifications_roles: browserNotificationsRoles.value,
  };
  // eslint-disable-next-line no-console
  console.log(
    '[Features] saveBrowserNotifications payload:',
    JSON.parse(JSON.stringify(payload))
  );
  try {
    const result = await store.dispatch('accounts/update', payload);
    // eslint-disable-next-line no-console
    console.log('[Features] saveBrowserNotifications result:', result);
    useAlert(t('FEATURES_SETTINGS.SAVE_SUCCESS'));
    // Request permission immediately if enabled
    if (browserNotificationsEnabled.value) {
      askBrowserNotificationPermission();
      requestPushPermissions({ onSuccess: () => {} });
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] saveBrowserNotifications error:', e);
    useAlert(t('FEATURES_SETTINGS.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

// --- Test de notificaciones de navegador (sobre el navegador del admin actual) ---

// Botón 1: pedir permiso al navegador para recibir notificaciones.
const requestNotificationPermission = () => {
  if (!('Notification' in window)) {
    useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.NO_API'));
    return;
  }
  // eslint-disable-next-line no-console
  console.log('[Features] permiso actual:', Notification.permission);
  if (Notification.permission === 'granted') {
    useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.ALREADY_GRANTED'));
    return;
  }
  if (Notification.permission === 'denied') {
    useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.DENIED'));
    return;
  }
  Notification.requestPermission().then(permission => {
    // eslint-disable-next-line no-console
    console.log('[Features] permiso tras solicitar:', permission);
    if (permission === 'granted') {
      useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.GRANTED'));
    } else {
      useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.DENIED'));
    }
  });
};

// Botón 2: relanzar todas las notificaciones acumuladas del usuario actual.
// Si no hay ninguna, lanza una de prueba. Se muestran como notificaciones
// nativas del navegador del propio admin (es donde se comprueba que funcionan).
const replayNotifications = async () => {
  if (!('Notification' in window)) {
    useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.NO_API'));
    return;
  }
  if (Notification.permission !== 'granted') {
    useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.NEED_PERMISSION'));
    return;
  }

  isTestingNotifications.value = true;
  try {
    const { data } = await NotificationsAPI.get({
      page: 1,
      sortOrder: 'desc',
    });
    const notifications = data?.data?.payload || [];
    // eslint-disable-next-line no-console
    console.log('[Features] notificaciones acumuladas:', notifications.length);

    if (!notifications.length) {
      // No hay ninguna acumulada → lanzar una de prueba.
      showTestNotification({
        title: t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.SAMPLE_TITLE'),
        body: t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.SAMPLE_BODY'),
      });
      useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.SENT_SAMPLE'));
      return;
    }

    // Relanzar cada notificación acumulada (máx 10 para no saturar).
    let shown = 0;
    notifications.slice(0, 10).forEach((n, idx) => {
      const conversationId = n.primary_actor?.id || n.primary_actor_id || null;
      const title = n.push_message_title || n.notification_type || 'Chatwoot';
      const body =
        n.push_message_body ||
        `${n.notification_type} #${conversationId || ''}`.trim();
      // Pequeño escalonado para que el SO no las colapse de golpe.
      setTimeout(() => {
        showTestNotification({
          title,
          body,
          conversationId,
          accountId: accountId.value,
        });
      }, idx * 400);
      shown += 1;
    });
    useAlert(
      t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.SENT_REPLAY', {
        count: shown,
      })
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] replayNotifications error:', e);
    useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.ERROR'));
  } finally {
    isTestingNotifications.value = false;
  }
};

// Botón 3: enviar una push de prueba a un usuario REMOTO en concreto vía Web Push.
// A diferencia de los otros botones (que prueban el navegador del propio admin),
// esto llega al dispositivo del usuario destino aunque no tenga Chatwoot abierto,
// siempre que tenga una suscripción Web Push activa.
const allAgents = computed(() => store.getters['agents/getAgents']);
const testPushUserId = ref(null);
// Opciones y etiqueta para el SelectMenu del usuario de prueba de push.
const testPushAgentOptions = computed(() =>
  allAgents.value.map(a => ({
    label: `${a.name} (${a.email})`,
    value: String(a.id),
  }))
);
const testPushUserLabel = computed(() => {
  const sel = testPushAgentOptions.value.find(
    o => o.value === String(testPushUserId.value)
  );
  return (
    sel?.label ||
    t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PUSH_SELECT_USER')
  );
});
const isSendingTestPush = ref(false);
const isSendingReminder = ref(false);

// kind 'test' → notificación de prueba; 'enable_reminder' → empujón para que
// el usuario active los permisos (al hacer click en la push se abre Chatwoot
// y le sale el banner "Activar notificaciones").
const sendTestPushToUser = async (kind = 'test') => {
  if (!testPushUserId.value) return;
  const flag =
    kind === 'enable_reminder' ? isSendingReminder : isSendingTestPush;
  flag.value = true;
  try {
    const { data } = await AgentAPI.testPush(testPushUserId.value, kind);
    // eslint-disable-next-line no-console
    console.log('[Features] sendTestPushToUser', kind, 'result:', data);
    if (!data.subscriptions) {
      useAlert(
        t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PUSH_NO_SUBSCRIPTION')
      );
    } else if (kind === 'enable_reminder') {
      useAlert(
        t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.REMINDER_SENT', {
          count: data.sent,
        })
      );
    } else {
      useAlert(
        t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PUSH_SENT', {
          count: data.sent,
        })
      );
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('[Features] sendTestPushToUser error:', e);
    useAlert(t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PUSH_ERROR'));
  } finally {
    flag.value = false;
  }
};

// --- Reset tour ---
const zohoAgents = computed(() =>
  store.getters['agents/getAgents'].filter(a => a.zoho_source)
);
const selectedAgentIds = ref([]);
const isResetting = ref(false);

const toggleAgent = id => {
  const idx = selectedAgentIds.value.indexOf(id);
  if (idx === -1) selectedAgentIds.value.push(id);
  else selectedAgentIds.value.splice(idx, 1);
};

const selectAll = () => {
  selectedAgentIds.value = zohoAgents.value.map(a => a.id);
};

const clearAll = () => {
  selectedAgentIds.value = [];
};

const resetTour = async () => {
  if (!selectedAgentIds.value.length) return;
  isResetting.value = true;
  try {
    const { reset_count: count } = await store.dispatch(
      'agents/resetTour',
      selectedAgentIds.value
    );
    useAlert(t('FEATURES_SETTINGS.RESET_TOUR.SUCCESS', { count }));
    selectedAgentIds.value = [];
  } catch {
    useAlert(t('FEATURES_SETTINGS.RESET_TOUR.ERROR'));
  } finally {
    isResetting.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col w-full max-w-2xl ltr:mr-auto rtl:ml-auto">
    <BaseSettingsHeader :title="$t('FEATURES_SETTINGS.TITLE')" />

    <div class="mt-4 space-y-4">
      <!-- Assignment email toggle -->
      <div
        class="flex items-start justify-between gap-4 rounded-xl border border-n-weak bg-n-background p-4"
      >
        <div class="flex-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('FEATURES_SETTINGS.ASSIGNMENT_EMAIL.TITLE') }}
          </p>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ $t('FEATURES_SETTINGS.ASSIGNMENT_EMAIL.DESCRIPTION') }}
          </p>
        </div>
        <label class="relative inline-flex cursor-pointer items-center">
          <input
            v-model="assignmentEmailEnabled"
            type="checkbox"
            class="peer sr-only"
            :disabled="isSaving"
            @change="
              saveToggle('assignment_email_enabled', assignmentEmailEnabled)
            "
          />
          <div
            class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
          />
        </label>
      </div>

      <!-- Zoho tour toggle -->
      <div
        class="flex items-start justify-between gap-4 rounded-xl border border-n-weak bg-n-background p-4"
      >
        <div class="flex-1">
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('FEATURES_SETTINGS.ZOHO_TOUR.TITLE') }}
          </p>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ $t('FEATURES_SETTINGS.ZOHO_TOUR.DESCRIPTION') }}
          </p>
        </div>
        <label class="relative inline-flex cursor-pointer items-center">
          <input
            v-model="zohoTourEnabled"
            type="checkbox"
            class="peer sr-only"
            :disabled="isSaving"
            @change="saveToggle('zoho_tour_enabled', zohoTourEnabled)"
          />
          <div
            class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
          />
        </label>
      </div>

      <!-- Visible WhatsApp template categories -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('FEATURES_SETTINGS.TEMPLATE_CATEGORIES.TITLE') }}
          </p>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ $t('FEATURES_SETTINGS.TEMPLATE_CATEGORIES.DESCRIPTION') }}
          </p>
        </div>
        <div class="rounded-lg border border-n-weak divide-y divide-n-weak">
          <div
            v-for="category in templateCategoryOptions"
            :key="category.id"
            class="flex items-start justify-between gap-4 px-3 py-2"
          >
            <div class="flex-1">
              <p class="text-sm text-n-slate-12">{{ category.title }}</p>
              <p class="mt-0.5 text-xs text-n-slate-11">
                {{ category.description }}
              </p>
            </div>
            <label class="relative inline-flex cursor-pointer items-center">
              <input
                type="checkbox"
                class="peer sr-only"
                :checked="isTemplateCategoryVisible(category.id)"
                :disabled="isSaving"
                @change="toggleTemplateCategory(category.id)"
              />
              <div
                class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
              />
            </label>
          </div>
        </div>
      </div>

      <!-- Browser notifications -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TITLE') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.DESCRIPTION') }}
            </p>
          </div>
          <label class="relative inline-flex cursor-pointer items-center">
            <input
              v-model="browserNotificationsEnabled"
              type="checkbox"
              class="peer sr-only"
              :disabled="isSaving"
              @change="
                saveToggle(
                  'browser_notifications_enabled',
                  browserNotificationsEnabled
                )
              "
            />
            <div
              class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
            />
          </label>
        </div>

        <template v-if="browserNotificationsEnabled">
          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.ROLES_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{
                $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.ROLES_DESCRIPTION')
              }}
            </p>
          </div>

          <div
            v-if="roleOptions.length"
            class="rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="role in roleOptions"
              :key="role.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="browserNotificationsRoles.includes(role.id)"
                :disabled="isSaving"
                @change="toggleRole(role.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                role.name
              }}</span>
            </label>
          </div>

          <!-- Test de notificaciones de navegador -->
          <div
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-2"
          >
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.TITLE') }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.DESCRIPTION')
              }}
            </p>
            <div class="flex flex-wrap gap-2 pt-1">
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="requestNotificationPermission"
              >
                {{
                  $t(
                    'FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PERMISSION_BUTTON'
                  )
                }}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isTestingNotifications"
                @click="replayNotifications"
              >
                {{
                  $t(
                    'FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.REPLAY_BUTTON'
                  )
                }}
              </button>
            </div>
          </div>

          <!-- Push de prueba a un usuario remoto en concreto (Web Push) -->
          <div
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-3 space-y-2"
          >
            <p class="text-xs font-medium text-n-slate-12">
              {{
                $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PUSH_TITLE')
              }}
            </p>
            <p class="text-xs text-n-slate-11">
              {{
                $t(
                  'FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PUSH_DESCRIPTION'
                )
              }}
            </p>
            <div class="flex flex-wrap items-center gap-2 pt-1">
              <SelectMenu
                :model-value="testPushUserId ? String(testPushUserId) : ''"
                :options="testPushAgentOptions"
                :label="testPushUserLabel"
                sub-menu-position="bottom"
                @update:model-value="
                  val => (testPushUserId = val ? Number(val) : null)
                "
              />
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isSendingTestPush || !testPushUserId"
                @click="sendTestPushToUser('test')"
              >
                {{
                  $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.PUSH_BUTTON')
                }}
              </button>
              <button
                type="button"
                class="inline-flex items-center gap-1.5 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-xs font-medium text-n-slate-12 transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
                :disabled="isSendingReminder || !testPushUserId"
                @click="sendTestPushToUser('enable_reminder')"
              >
                {{
                  $t(
                    'FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.TEST.REMINDER_BUTTON'
                  )
                }}
              </button>
            </div>
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="saveBrowserNotifications"
            >
              {{ $t('FEATURES_SETTINGS.BROWSER_NOTIFICATIONS.SAVE_BUTTON') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Templates section -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.TEMPLATES_SECTION.TITLE') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.TEMPLATES_SECTION.DESCRIPTION') }}
            </p>
          </div>
          <label class="relative inline-flex cursor-pointer items-center">
            <input
              v-model="templatesSectionEnabled"
              type="checkbox"
              class="peer sr-only"
              :disabled="isSaving"
              @change="
                saveToggle('templates_section_enabled', templatesSectionEnabled)
              "
            />
            <div
              class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
            />
          </label>
        </div>

        <template v-if="templatesSectionEnabled">
          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.TEMPLATES_SECTION.ROLES_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.TEMPLATES_SECTION.ROLES_DESCRIPTION') }}
            </p>
          </div>

          <div
            v-if="roleOptions.length"
            class="rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="role in roleOptions"
              :key="role.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="templatesSectionRoles.includes(role.id)"
                :disabled="isSaving"
                @change="toggleTemplatesRole(role.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                role.name
              }}</span>
            </label>
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="saveTemplatesSection"
            >
              {{ $t('FEATURES_SETTINGS.TEMPLATES_SECTION.SAVE_BUTTON') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Gestión Plus -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.GESTION_PLUS.TITLE') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.GESTION_PLUS.DESCRIPTION') }}
            </p>
          </div>
          <label class="relative inline-flex cursor-pointer items-center">
            <input
              v-model="gestionPlusEnabled"
              type="checkbox"
              class="peer sr-only"
              :disabled="isSaving"
              @change="saveToggle('gestion_plus_enabled', gestionPlusEnabled)"
            />
            <div
              class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
            />
          </label>
        </div>

        <template v-if="gestionPlusEnabled">
          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.GESTION_PLUS.ROLES_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.GESTION_PLUS.ROLES_DESCRIPTION') }}
            </p>
          </div>

          <div
            v-if="roleOptions.length"
            class="rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="role in roleOptions"
              :key="role.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="gestionPlusRoles.includes(role.id)"
                :disabled="isSaving"
                @change="toggleGestionPlusRole(role.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                role.name
              }}</span>
            </label>
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="saveGestionPlus"
            >
              {{ $t('FEATURES_SETTINGS.GESTION_PLUS.SAVE_BUTTON') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Toggle de marcar como leído -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.MARK_AS_READ_TOGGLE.TITLE') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.MARK_AS_READ_TOGGLE.DESCRIPTION') }}
            </p>
          </div>
          <label class="relative inline-flex cursor-pointer items-center">
            <input
              v-model="markAsReadToggleEnabled"
              type="checkbox"
              class="peer sr-only"
              :disabled="isSaving"
              @change="
                saveToggle(
                  'mark_as_read_toggle_enabled',
                  markAsReadToggleEnabled
                )
              "
            />
            <div
              class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
            />
          </label>
        </div>

        <template v-if="markAsReadToggleEnabled">
          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.MARK_AS_READ_TOGGLE.ROLES_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{
                $t('FEATURES_SETTINGS.MARK_AS_READ_TOGGLE.ROLES_DESCRIPTION')
              }}
            </p>
          </div>

          <div
            v-if="roleOptions.length"
            class="rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="role in roleOptions"
              :key="role.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="markAsReadToggleRoles.includes(role.id)"
                :disabled="isSaving"
                @change="toggleMarkAsReadRole(role.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                role.name
              }}</span>
            </label>
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="saveMarkAsReadToggle"
            >
              {{ $t('FEATURES_SETTINGS.MARK_AS_READ_TOGGLE.SAVE_BUTTON') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Sincronización de notas con CRM -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.CRM_NOTES_SYNC.TITLE') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.CRM_NOTES_SYNC.DESCRIPTION') }}
            </p>
          </div>
          <label class="relative inline-flex cursor-pointer items-center">
            <input
              v-model="crmNotesSyncEnabled"
              type="checkbox"
              class="peer sr-only"
              :disabled="isSaving"
              @change="
                saveToggle('crm_notes_sync_enabled', crmNotesSyncEnabled)
              "
            />
            <div
              class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
            />
          </label>
        </div>

        <template v-if="crmNotesSyncEnabled">
          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.CRM_NOTES_SYNC.ROLES_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.CRM_NOTES_SYNC.ROLES_DESCRIPTION') }}
            </p>
          </div>

          <div
            v-if="roleOptions.length"
            class="rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="role in roleOptions"
              :key="role.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="crmNotesSyncRoles.includes(role.id)"
                :disabled="isSaving"
                @change="toggleCrmNotesSyncRole(role.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                role.name
              }}</span>
            </label>
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="saveCrmNotesSync"
            >
              {{ $t('FEATURES_SETTINGS.CRM_NOTES_SYNC.SAVE_BUTTON') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Sync IA (backfill de mensajes de la IA desde Twilio) -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.AI_BACKFILL.TITLE') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.AI_BACKFILL.DESCRIPTION') }}
            </p>
          </div>
          <label class="relative inline-flex cursor-pointer items-center">
            <input
              v-model="aiBackfillEnabled"
              type="checkbox"
              class="peer sr-only"
              :disabled="isSaving"
              @change="saveToggle('ai_backfill_enabled', aiBackfillEnabled)"
            />
            <div
              class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
            />
          </label>
        </div>

        <template v-if="aiBackfillEnabled">
          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.AI_BACKFILL.ROLES_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.AI_BACKFILL.ROLES_DESCRIPTION') }}
            </p>
          </div>

          <div
            v-if="roleOptions.length"
            class="rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="role in roleOptions"
              :key="role.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="aiBackfillRoles.includes(role.id)"
                :disabled="isSaving"
                @change="toggleAiBackfillRole(role.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                role.name
              }}</span>
            </label>
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="saveAiBackfill"
            >
              {{ $t('FEATURES_SETTINGS.AI_BACKFILL.SAVE_BUTTON') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Traducción automática (pestaña Traducción en el cuadro de respuesta) -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-4"
      >
        <div class="flex items-start justify-between gap-4">
          <div class="flex-1">
            <p class="text-sm font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.AUTO_TRANSLATION.TITLE') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.AUTO_TRANSLATION.DESCRIPTION') }}
            </p>
          </div>
          <label class="relative inline-flex cursor-pointer items-center">
            <input
              v-model="autoTranslationEnabled"
              type="checkbox"
              class="peer sr-only"
              :disabled="isSaving"
              @change="
                saveToggle('auto_translation_enabled', autoTranslationEnabled)
              "
            />
            <div
              class="peer h-5 w-9 rounded-full bg-n-slate-5 transition-colors peer-checked:bg-n-brand peer-disabled:opacity-50 after:absolute after:left-0.5 after:top-0.5 after:h-4 after:w-4 after:rounded-full after:bg-white after:transition-all peer-checked:after:translate-x-4"
            />
          </label>
        </div>

        <template v-if="autoTranslationEnabled">
          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.AUTO_TRANSLATION.ROLES_LABEL') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{ $t('FEATURES_SETTINGS.AUTO_TRANSLATION.ROLES_DESCRIPTION') }}
            </p>
          </div>

          <div
            v-if="roleOptions.length"
            class="rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="role in roleOptions"
              :key="role.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="autoTranslationRoles.includes(role.id)"
                :disabled="isSaving"
                @change="toggleAutoTranslationRole(role.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12">{{
                role.name
              }}</span>
            </label>
          </div>

          <div>
            <p class="text-xs font-medium text-n-slate-12">
              {{ $t('FEATURES_SETTINGS.AUTO_TRANSLATION.TEST_CONVERSATION') }}
            </p>
            <p class="mt-0.5 text-xs text-n-slate-11">
              {{
                $t(
                  'FEATURES_SETTINGS.AUTO_TRANSLATION.TEST_CONVERSATION_DESCRIPTION'
                )
              }}
            </p>
            <input
              v-model="autoTranslationTestConversationId"
              type="text"
              inputmode="numeric"
              class="mt-2 w-48 rounded-lg border border-n-weak bg-n-background px-3 py-1.5 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand"
              :placeholder="
                $t(
                  'FEATURES_SETTINGS.AUTO_TRANSLATION.TEST_CONVERSATION_PLACEHOLDER'
                )
              "
              :disabled="isSaving"
            />
          </div>

          <div class="flex justify-end">
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isSaving"
              @click="saveAutoTranslation"
            >
              {{ $t('FEATURES_SETTINGS.AUTO_TRANSLATION.SAVE_BUTTON') }}
            </button>
          </div>
        </template>
      </div>

      <!-- Reset tour -->
      <div
        class="rounded-xl border border-n-weak bg-n-background p-4 space-y-3"
      >
        <div>
          <p class="text-sm font-medium text-n-slate-12">
            {{ $t('FEATURES_SETTINGS.RESET_TOUR.TITLE') }}
          </p>
          <p class="mt-0.5 text-xs text-n-slate-11">
            {{ $t('FEATURES_SETTINGS.RESET_TOUR.DESCRIPTION') }}
          </p>
        </div>

        <p
          v-if="zohoAgents.length === 0"
          class="text-xs text-n-slate-10 italic"
        >
          {{ $t('FEATURES_SETTINGS.RESET_TOUR.NO_ZOHO_AGENTS') }}
        </p>

        <template v-else>
          <div
            class="max-h-48 overflow-y-auto rounded-lg border border-n-weak divide-y divide-n-weak"
          >
            <label
              v-for="agent in zohoAgents"
              :key="agent.id"
              class="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-n-alpha-1 transition-colors"
            >
              <Checkbox
                :model-value="selectedAgentIds.includes(agent.id)"
                @change="toggleAgent(agent.id)"
              />
              <span class="flex-1 text-sm text-n-slate-12 truncate">
                {{ agent.name }}
              </span>
              <span class="text-xs text-n-slate-10 truncate">
                {{ agent.email }}
              </span>
            </label>
          </div>

          <div class="flex items-center justify-between gap-2">
            <div class="flex gap-2">
              <button
                type="button"
                class="text-xs text-n-brand hover:underline"
                @click="selectAll"
              >
                {{ $t('FEATURES_SETTINGS.RESET_TOUR.SELECT_ALL') }}
              </button>

              <button
                type="button"
                class="text-xs text-n-slate-11 hover:underline"
                @click="clearAll"
              >
                {{ $t('FEATURES_SETTINGS.RESET_TOUR.CLEAR') }}
              </button>
            </div>
            <button
              type="button"
              class="inline-flex items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              :disabled="isResetting || selectedAgentIds.length === 0"
              @click="resetTour"
            >
              {{ $t('FEATURES_SETTINGS.RESET_TOUR.BUTTON') }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
